import { HttpAgent, RunAgentInput } from '@ag-ui/client';
import { RunStartedEvent } from '@ag-ui/core';
import { isEmpty, isNil, isString } from 'lodash';
import { AskScope, buildAgUiRequestBody, postAgUiPrompt } from '../api';
import { ASK_GATEWAY_BASE_URL } from '../api/config';
import { withChatDirectives } from '../util/withAskDirectives';
import { lastUserPrompt } from './lastUserPrompt';

/**
 * What `threadId` holds until the gateway has issued one — a conversation nobody has asked a
 * question in yet, and a first turn that therefore names no thread.
 *
 * A value rather than the absence of one because AG-UI's `AbstractAgent` types `threadId` as a
 * required `string` and mints a uuid of its own when handed nothing, which is exactly what this
 * has to stop. Empty is the value it can be, since `buildAgUiRequestBody` leaves an empty thread
 * id off the body.
 */
const NO_THREAD_ID = '';

interface CoastAskAgentConfig {
  /** Resolves the current business id at run time (may change as the user switches orgs). */
  getBusinessId: () => number | undefined;
  /** Resolves the active scope at run time (set when the panel is opened). */
  getScope: () => AskScope;
  /**
   * Turns the newest user turn into the prompt that goes on the wire. Defaults to the panel's
   * directives; the summary widgets want a different answer from the same gateway, so they
   * pass their own (see `withSummaryDirectives`).
   */
  decoratePrompt?: (prompt: string, scope: AskScope) => string;
}

/**
 * Drives the Coast Ask gateway with `protocol: 'ag-ui'`, so the gateway streams
 * AG-UI events and `HttpAgent` handles SSE parsing, protocol verification and
 * event → `Message[]` assembly (text streaming, tool-call argument
 * accumulation, tool results, reasoning) for us.
 *
 * Three things differ from a stock `HttpAgent`:
 *
 * - The gateway takes `{ prompt, threadId, scope, protocol }` rather than a `RunAgentInput`,
 *   so `requestInit` rewrites the body: the newest user turn becomes `prompt`, and the turns
 *   before it are the gateway's own to remember, held against the thread id.
 * - Requests need a short-lived prompt token, minted asynchronously and retried
 *   on 401/403, which is what the injected `fetch` does.
 * - The thread id belongs to the gateway rather than to AG-UI, which mints one per agent:
 *   a new conversation asks with none and takes the one the answer carries (see
 *   `adoptThreadId`).
 */
export class CoastAskAgent extends HttpAgent {
  private readonly getBusinessId: () => number | undefined;
  private readonly getScope: () => AskScope;
  private readonly decoratePrompt: (prompt: string, scope: AskScope) => string;

  constructor({ getBusinessId, getScope, decoratePrompt }: CoastAskAgentConfig) {
    // `threadId` is passed explicitly so `AbstractAgent` does not mint one: handed nothing, it
    // falls back to a uuid of its own, and that uuid would go on the wire as a thread the
    // gateway never issued.
    super({
      url: `${ASK_GATEWAY_BASE_URL}/prompt`,
      debug: import.meta.env.DEV,
      threadId: NO_THREAD_ID,
    });
    this.getBusinessId = getBusinessId;
    this.getScope = getScope;
    this.decoratePrompt = decoratePrompt ?? withChatDirectives;

    // Assigned after `super()` because `this` is unavailable in the argument list.
    this.fetch = (url, requestInit) => {
      const businessId = this.getBusinessId();
      if (isNil(businessId)) {
        return Promise.reject(new Error('Cannot run Ask without an active business'));
      }
      return postAgUiPrompt({
        businessId,
        url,
        body: String(requestInit.body),
        signal: requestInit.signal ?? undefined,
      });
    };

    this.subscribe({
      onRunStartedEvent: ({ event }) => this.adoptThreadId(event),
    });
  }

  /**
   * Starts a fresh conversation: empty transcript, no thread id. The agent is created once
   * and outlives every conversation, so ending one has to be explicit — carrying a thread id
   * across a cleared conversation would have the gateway continue the conversation the user
   * just cleared.
   */
  resetConversation(): void {
    this.abortRun();
    this.setMessages([]);
    this.threadId = NO_THREAD_ID;
  }

  protected requestInit(input: RunAgentInput): RequestInit {
    const scope = this.getScope();

    return {
      ...super.requestInit(input),
      // AG-UI keeps the user's turn verbatim for rendering; only what goes on the wire
      // carries the directives (card links, response language).
      body: JSON.stringify(
        buildAgUiRequestBody({
          prompt: this.decoratePrompt(lastUserPrompt(input.messages), scope),
          scope,
          // The agent's own field rather than `input.threadId`, which is a copy of it taken
          // when the run was prepared: this is the gateway's id for the conversation, and the
          // agent is where it is kept between turns.
          threadId: this.threadId,
        }),
      ),
    };
  }

  /**
   * Takes the thread id the gateway issued for this conversation, so every turn after the
   * first one names it. A new conversation asks with none, which is what tells the gateway to
   * start one.
   *
   * **This is the only place that knows where in the response the thread id lives.** It rides
   * on `RUN_STARTED` — a uuid, echoed unchanged on `RUN_FINISHED` — so it is in hand before
   * the answer has finished streaming. Only the start is read: AG-UI's schema makes `threadId`
   * required on it, so there is no case where the finish would carry one the start did not.
   *
   * The guard is against an empty value rather than a missing one, for the same reason: a
   * gateway that stopped filling it in would send `''`, and taking that would silently turn
   * every following turn back into a first turn.
   *
   * Whatever the newest run says wins, so a gateway that re-keys a conversation keeps the
   * next turn attached to it.
   */
  private adoptThreadId(event: RunStartedEvent): void {
    const { threadId } = event;
    if (isString(threadId) && !isEmpty(threadId)) {
      this.threadId = threadId;
    }
  }
}
